import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { getDashboardStats, globalSearch } from '../../services/adminApi';
import adminApi from '../../services/adminApi';
import toast from 'react-hot-toast';

const NAV = [
  {
    group: 'Overview',
    items: [
      { to: '/admin/dashboard',     icon: 'ti-layout-dashboard', label: 'Dashboard' },
    ],
  },
  {
    group: 'Content',
    items: [
      { to: '/admin/roles',         icon: 'ti-id-badge',         label: 'Job roles' },
      { to: '/admin/chapters',      icon: 'ti-list',             label: 'Chapters' },
      { to: '/admin/sub-chapters',  icon: 'ti-layout-grid',      label: 'Sub-chapters' },
      { to: '/admin/questions',     icon: 'ti-help-circle',      label: 'Question bank' },
      { to: '/admin/review-queue',  icon: 'ti-checks',           label: 'Review queue' },
      { to: '/admin/bulk-upload',    icon: 'ti-upload',           label: 'Bulk upload' },
      { to: '/admin/study-content',   icon: 'ti-book',             label: 'Study content' },
      { to: '/admin/explain-content', icon: 'ti-bulb',             label: 'Explain content' },
    ],
  },
  {
    group: 'Study Guides',
    items: [
      { to: '/admin/courses',          icon: 'ti-school',           label: 'All study guides' },
      { to: '/admin/courses/generate', icon: 'ti-sparkles',         label: 'Generate study guide' },
    ],
  },
  {
    group: 'Users',
    items: [
      { to: '/admin/users',         icon: 'ti-users',            label: 'All users' },
      { to: '/admin/support',       icon: 'ti-headset',          label: 'Support inbox' },
    ],
  },
  {
    group: 'Revenue',
    items: [
      { to: '/admin/subscriptions', icon: 'ti-repeat',           label: 'Subscriptions' },
      { to: '/admin/payments',      icon: 'ti-receipt',          label: 'Payments' },
      { to: '/admin/refunds',       icon: 'ti-arrow-back-up',    label: 'Refunds' },
    ],
  },
  {
    group: 'Analytics',
    items: [
      { to: '/admin/analytics/users',      icon: 'ti-chart-bar',   label: 'User analytics' },
      { to: '/admin/analytics/revenue',    icon: 'ti-coin',         label: 'Revenue analytics' },
      { to: '/admin/analytics/content',    icon: 'ti-school',       label: 'Content analytics' },
      { to: '/admin/analytics/conversion', icon: 'ti-funnel',       label: 'Conversion funnel' },
    ],
  },
  {
    group: 'Certificates',
    items: [
      { to: '/admin/certificates',  icon: 'ti-certificate',      label: 'Issued certificates' },
    ],
  },
  {
    group: 'Marketing',
    items: [
      { to: '/admin/coupons',         icon: 'ti-discount',         label: 'Coupons' },
      { to: '/admin/vouchers',        icon: 'ti-ticket',           label: 'Vouchers' },
      { to: '/admin/withdrawals',     icon: 'ti-cash',             label: 'Withdrawals' },
      { to: '/admin/broadcast',       icon: 'ti-send',             label: 'Broadcast email' },
      { to: '/admin/email-templates', icon: 'ti-mail-cog',         label: 'Email templates' },
      { to: '/admin/blog',            icon: 'ti-article',          label: 'Blog CMS' },
      { to: '/admin/jd-prep',         icon: 'ti-file-description', label: 'JD Prep sessions' },
    ],
  },
  {
    group: 'System',
    items: [
      { to: '/admin/content-health', icon: 'ti-heartbeat',       label: 'Content health' },
      { to: '/admin/activity-log',   icon: 'ti-clipboard-list',  label: 'Audit log' },
      { to: '/admin/export',         icon: 'ti-download',        label: 'Export data' },
    ],
  },
  {
    group: 'Business',
    items: [
      { to: '/admin/companies',        icon: 'ti-building-skyscraper', label: 'Companies' },
      { to: '/admin/company-coupons',  icon: 'ti-discount-2',          label: 'B2B Coupons' },
    ],
  },
  {
    group: 'Admin',
    items: [
      { to: '/admin/admins',        icon: 'ti-user-shield',      label: 'Admin users' },
      { to: '/admin/settings',      icon: 'ti-settings',         label: 'Settings' },
    ],
  },
];

export default function AdminLayout({ children }) {
  const { admin, adminLogout } = useAdminAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 768);
  const [sidebarOpen,  setSidebarOpen]  = useState(window.innerWidth >= 768);
  const [pendingDrafts, setPendingDrafts] = useState(0);
  const [expiringWeek,  setExpiringWeek]  = useState(0);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching,     setSearching]     = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [pwModal,   setPwModal]   = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew,     setPwNew]     = useState('');
  const [pwSaving,  setPwSaving]  = useState(false);

  useEffect(() => {
    getDashboardStats().then(r => {
      setPendingDrafts(r.data.questions_draft || 0);
      setExpiringWeek(r.data.expiring_this_week || 0);
    }).catch(() => {});
  }, [location.pathname]);

  // Close search + sidebar (mobile) on route change
  useEffect(() => {
    setSearchResults(null);
    setSearchQuery('');
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchResults(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.trim().length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const { data } = await globalSearch(q);
      setSearchResults(data);
    } catch {}
    setSearching(false);
  };

  const handleLogout = async () => {
    await adminLogout();
    toast.success('Logged out');
    navigate('/admin/login');
  };

  const handleChangePassword = async () => {
    if (!pwCurrent || !pwNew) return toast.error('Both fields are required');
    setPwSaving(true);
    try {
      await adminApi.post('/admin/auth/change-password', { current_password: pwCurrent, new_password: pwNew });
      toast.success('Password changed');
      setPwModal(false); setPwCurrent(''); setPwNew('');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setPwSaving(false);
  };

  const hasResults = searchResults && (searchResults.users.length + searchResults.roles.length + searchResults.questions.length > 0);

  return (
    <>
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f4f0' }}>

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, backdropFilter: 'blur(2px)' }} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside style={{
        width: isMobile ? 260 : (sidebarOpen ? 240 : 64),
        background: '#1B3A5C',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: isMobile ? 'left 0.25s ease' : 'width 0.2s ease',
        overflow: 'hidden',
        // Mobile: fixed overlay sliding in from left
        ...(isMobile ? {
          position: 'fixed',
          top: 0,
          left: sidebarOpen ? 0 : -260,
          height: '100vh',
          zIndex: 1000,
          boxShadow: sidebarOpen ? '4px 0 24px rgba(0,0,0,0.25)' : 'none',
        } : {}),
      }}>

        {/* Logo row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 56, borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}>
          {(sidebarOpen || isMobile) && (
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap' }}>
              Centric<span style={{ color: '#C1272D' }}>Q</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 400, marginLeft: 6 }}>Admin</span>
            </span>
          )}
          <button
            onClick={() => setSidebarOpen(o => !o)}
            aria-label="Toggle sidebar"
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
                     cursor: 'pointer', padding: 4, display: 'flex', marginLeft: 'auto' }}
          >
            <i className={`ti ${sidebarOpen ? 'ti-layout-sidebar-left-collapse' : 'ti-layout-sidebar-left-expand'}`}
               style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {NAV.map(({ group, items }) => (
            <div key={group}>
              {(sidebarOpen || isMobile) && (
                <p style={{
                  fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                  color: 'rgba(255,255,255,0.3)', padding: '16px 20px 4px',
                  textTransform: 'uppercase', margin: 0,
                }}>
                  {group}
                </p>
              )}
              {items.map(({ to, icon, label }) => (
                <NavLink
                  key={to} to={to}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: (sidebarOpen || isMobile) ? '8px 20px' : '8px 0',
                    justifyContent: (sidebarOpen || isMobile) ? 'flex-start' : 'center',
                    textDecoration: 'none', fontSize: 14,
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                    borderLeft: isActive ? '3px solid #C1272D' : '3px solid transparent',
                    transition: 'all 0.15s',
                  })}
                >
                  <i className={`ti ${icon}`} style={{ fontSize: 18, flexShrink: 0 }} />
                  {(sidebarOpen || isMobile) && <span style={{ whiteSpace: 'nowrap' }}>{label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Admin user row */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#C1272D', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 600, flexShrink: 0,
          }}>
            {admin?.full_name?.charAt(0) || 'A'}
          </div>
          {(sidebarOpen || isMobile) && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#fff', fontWeight: 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {admin?.full_name}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                {admin?.permission_level}
              </p>
            </div>
          )}
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            <button onClick={() => setPwModal(true)} title="Change password"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4, display: 'flex' }}>
              <i className="ti ti-key" style={{ fontSize: 17 }} />
            </button>
            <button onClick={handleLogout} title="Log out"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4, display: 'flex' }}>
              <i className="ti ti-logout" style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Topbar */}
        <header style={{
          height: 56, background: '#fff', borderBottom: '1px solid #e8e6df',
          display: 'flex', alignItems: 'center', padding: isMobile ? '0 16px' : '0 24px',
          justifyContent: 'space-between', flexShrink: 0, gap: 12,
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <button onClick={() => setSidebarOpen(o => !o)} aria-label="Open menu"
              style={{ background: 'none', border: 'none', color: '#1B3A5C', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0 }}>
              <i className="ti ti-menu-2" style={{ fontSize: 22 }} />
            </button>
          )}
          {/* Alerts */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {pendingDrafts > 0 && (
              <NavLink to="/admin/review-queue" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#534AB7', textDecoration: 'none', background: '#EEEDFE', padding: '4px 10px', borderRadius: 20 }}>
                <i className="ti ti-checks" style={{ fontSize: 13 }} />
                {pendingDrafts} draft{pendingDrafts !== 1 ? 's' : ''}
              </NavLink>
            )}
            {expiringWeek > 0 && (
              <NavLink to="/admin/users?tab=at-risk" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#854F0B', textDecoration: 'none', background: '#FAEEDA', padding: '4px 10px', borderRadius: 20 }}>
                <i className="ti ti-clock" style={{ fontSize: 13 }} />
                {expiringWeek} expiring
              </NavLink>
            )}
          </div>

          {/* Global search — hidden on mobile */}
          <div ref={searchRef} style={{ position: 'relative', flex: 1, maxWidth: 320, display: isMobile ? 'none' : 'block' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14, pointerEvents: 'none' }} />
            <input
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              onFocus={() => searchQuery.length >= 2 && handleSearch(searchQuery)}
              placeholder="Search users, roles, questions…"
              style={{ width: '100%', padding: '7px 12px 7px 30px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', background: '#f9f8f5', boxSizing: 'border-box' }}
            />
            {searching && <i className="ti ti-loader-2" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }} />}

            {searchResults && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 6, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1000, overflow: 'hidden', maxHeight: 380, overflowY: 'auto' }}>
                {!hasResults ? (
                  <div style={{ padding: '14px 16px', fontSize: 13, color: '#9ca3af' }}>No results for "{searchQuery}"</div>
                ) : (
                  <>
                    {searchResults.users.length > 0 && (
                      <div>
                        <p style={{ margin: 0, padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Users</p>
                        {searchResults.users.map(u => (
                          <Link key={u.id} to="/admin/users" state={{ highlight: u.id }} onClick={() => setSearchResults(null)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', textDecoration: 'none', color: '#374151', fontSize: 13 }}>
                            <i className="ti ti-user" style={{ color: '#9ca3af', fontSize: 15 }} />
                            <span><strong>{u.full_name}</strong> <span style={{ color: '#9ca3af' }}>{u.email}</span></span>
                          </Link>
                        ))}
                      </div>
                    )}
                    {searchResults.roles.length > 0 && (
                      <div>
                        <p style={{ margin: 0, padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Job roles</p>
                        {searchResults.roles.map(r => (
                          <Link key={r.id} to="/admin/roles" onClick={() => setSearchResults(null)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', textDecoration: 'none', color: '#374151', fontSize: 13 }}>
                            <i className="ti ti-id-badge" style={{ color: '#9ca3af', fontSize: 15 }} />
                            <span><strong>{r.title}</strong> <span style={{ color: '#9ca3af' }}>{r.category}</span></span>
                            {!r.is_active && <span style={{ marginLeft: 'auto', fontSize: 10, background: '#f3f4f6', color: '#9ca3af', padding: '1px 6px', borderRadius: 8 }}>Inactive</span>}
                          </Link>
                        ))}
                      </div>
                    )}
                    {searchResults.questions.length > 0 && (
                      <div>
                        <p style={{ margin: 0, padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Questions</p>
                        {searchResults.questions.map(q => (
                          <Link key={q.id} to="/admin/questions" onClick={() => setSearchResults(null)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', textDecoration: 'none', color: '#374151', fontSize: 13 }}>
                            <i className="ti ti-help-circle" style={{ color: '#9ca3af', fontSize: 15 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.content}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {!isMobile && (
            <a href="/" target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: '#1B3A5C', textDecoration: 'none',
                       display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <i className="ti ti-external-link" style={{ fontSize: 15 }} />
              View site
            </a>
          )}
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: isMobile ? '20px 16px' : '28px 32px', overflowY: 'auto' }}>
          {children}
        </main>

      </div>
    </div>


    {/* ── Change password modal ────────────────────────────────────────────── */}
    {pwModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setPwModal(false)}>
        <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1B3A5C' }}>Change password</h3>
            <button onClick={() => setPwModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}>
              <i className="ti ti-x" />
            </button>
          </div>
          {[
            { label: 'Current password', value: pwCurrent, set: setPwCurrent },
            { label: 'New password',     value: pwNew,     set: setPwNew     },
          ].map(f => (
            <div key={f.label} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{f.label}</label>
              <input
                type="password"
                value={f.value}
                onChange={e => f.set(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          ))}
          <p style={{ margin: '0 0 18px', fontSize: 11, color: '#9ca3af' }}>Min 8 chars · one uppercase · one number</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setPwModal(false)} style={{ padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleChangePassword} disabled={pwSaving} style={{ padding: '8px 20px', background: '#1B3A5C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {pwSaving ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
